import React, { useContext, useState } from 'react';
import { Project, StateContext } from '../../StateContext';

function getFirstDayOfISOWeek(weekNumber: number, year: number): Date {
    const jan4 = new Date(year, 0, 4);
    const dayOfWeek = jan4.getDay() || 7;
    const week1Mon = new Date(year, 0, 4 - dayOfWeek + 1);
    const result = new Date(week1Mon);
    result.setDate(week1Mon.getDate() + (weekNumber - 1) * 7);
    return result;
}

function getProjectMonthKey(project: Project): string {
    let earliest: { weekNumber: number; year: number } | null = null;
    for (const link of project.links ?? []) {
        for (const wd of link.weekData ?? []) {
            // Use stored year if available, fall back to created timestamp for old records
            const year = wd.year || new Date(wd.created).getFullYear();
            if (!earliest || year < earliest.year || (year === earliest.year && wd.weekNumber < earliest.weekNumber)) {
                earliest = { weekNumber: wd.weekNumber, year };
            }
        }
    }
    if (!earliest) return 'no-data';
    const date = getFirstDayOfISOWeek(earliest.weekNumber, earliest.year);
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    return `${y}-${m < 10 ? '0' + m : m}`;
}

function formatMonthKey(key: string): string {
    if (key === 'no-data') return 'Geen data';
    const [year, month] = key.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleString('nl-NL', { month: 'long', year: 'numeric' });
}
import ExpandableRow from '../../General/ExpandableRow';
import ExpendableBox from '../../General/ExpandableBox';
import { deleteProjectCall, editProjectCall, updateProjectExtrasCall } from '../../Services/APICalls/apicalls';
import EditProject from './editProject';
import ConnectMechanic from './connectMechanic';
import LinkView from '../../General/Links/linkView';
import { totalProjectHours, secondsToHours, totalProjectBaseline, hoursForType } from '../../General/Links/weekView';
import { IContextData } from '../../../App';
import TotalHourOverview from '../../General/TotalHourOverview';
import ToDoList from './To-do list/toDoList';
import FilterProjectList from './filterProjectList';
import { useLanguage } from '../../Services/LanguageContext';
import ProjectExtrasModal from './ProjectExtrasModal';
import ProjectNotesModal from './ProjectNotesModal';

const hoursToSeconds = (h: number) => Math.round(h * 3600);

// Shared style for the three status buttons
const statusButton = (active: boolean, color: string): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '5px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    border: `1.5px solid ${active ? color : '#555'}`,
    background: active ? `${color}22` : '#2a2d3a',
    color: active ? color : '#aaa',
    transition: 'all 0.2s ease',
    userSelect: 'none' as const,
    boxShadow: active ? `0 0 6px ${color}44` : '0 1px 3px rgba(0,0,0,0.35)',
});

const dot = (color: string): React.CSSProperties => ({
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    background: color,
    flexShrink: 0,
});

const checkbox = (active: boolean, color: string): React.CSSProperties => ({
    width: '13px',
    height: '13px',
    border: `1.5px solid ${active ? color : '#888'}`,
    borderRadius: '3px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '9px',
    flexShrink: 0,
    color: active ? color : '#888',
});

const ProjectList = () => {
    const { loginData, setLoginData } = useContext<IContextData>(StateContext);
    const [query, setQuery] = useState('');
    const [collapsedMonths, setCollapsedMonths] = useState<Set<string>>(new Set());
    const { t } = useLanguage();

    const toggleMonth = (key: string) =>
        setCollapsedMonths((prev) => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });

    // Track which modal is open per project: null | 'meerwerk' | 'dhz'
    const [modalState, setModalState] = useState<{ projectId: number; type: 'meerwerk' | 'dhz' } | null>(null);

    const handleMaterialsToggle = (p: Project) => {
        editProjectCall(p.id, p.name, p.estimatedTimeInSeconds, p.about, setLoginData, !p.materialsDelivered);
    };

    const handleSaveExtras = (p: Project, type: 'meerwerk' | 'dhz', newHours: number) => {
        const meerwerkSeconds = type === 'meerwerk' ? hoursToSeconds(newHours) : (p.meerwerkSeconds ?? 0);
        const dhzSeconds      = type === 'dhz'      ? hoursToSeconds(newHours) : (p.dhzSeconds ?? 0);
        updateProjectExtrasCall(p.id, meerwerkSeconds, dhzSeconds, setLoginData);
    };

    const projects = (loginData.projects as Project[]).filter((p) =>
        p.name.toLowerCase().includes(query.toLowerCase())
    );

    const renderButtons = (p: Project) => (
        <React.Fragment>
            <ConnectMechanic id={p.id} />
            <EditProject
                id={p.id}
                name={p.name}
                about={p.about}
                estimatedTimeInSeconds={p.estimatedTimeInSeconds}
                materialsDelivered={p.materialsDelivered}
                types={p.types ?? []}
            />
        </React.Fragment>
    );

    const sortByMechanicName = (a, b) => a.mechanic.name.localeCompare(b.mechanic.name);

    const activeModal = modalState
        ? projects.find((p) => p.id === modalState.projectId)
        : null;

    const renderUntypedMechanics = (p: Project) => {
        const untypedLinks = (p.links ?? []).filter(link =>
            link.weekData.some(w => w.projectTypeId == null)
        );
        if (untypedLinks.length === 0) return null;
        return (
            <ExpandableRow key='untyped' Title={t('noType')}>
                {untypedLinks.sort(sortByMechanicName).map(link => (
                    <LinkView key={link.id} link={link} project={p} filterTypeId={null} />
                ))}
            </ExpandableRow>
        );
    };

    const renderTypedMechanics = (p: Project) => {
        return (p.types ?? []).map((pt) => {
            const relevantLinks = (p.links ?? []).filter(link =>
                link.projectTypeId === pt.id ||
                link.weekData.some(w => w.projectTypeId === pt.id)
            );
            const typeEstimated = secondsToHours(pt.calculatedTimeInSeconds);
            const typeActual = hoursForType(p.links ?? [], pt.id);
            const typeRemaining = typeEstimated - typeActual;
            return (
                <ExpandableRow key={pt.id} Title={pt.name} total={typeRemaining}>
                    {relevantLinks.sort(sortByMechanicName).map(link => (
                        <LinkView key={link.id} link={link} project={p} filterTypeId={pt.id} />
                    ))}
                </ExpandableRow>
            );
        });
    };

    const renderProject = (p: Project) => {
        const hasTypes = (p.types ?? []).length > 0;
        const baseline = totalProjectBaseline(p.links);
        const estimatedHours = hasTypes
            ? (p.types ?? []).reduce((sum, pt) => sum + secondsToHours(pt.calculatedTimeInSeconds), 0)
            : secondsToHours(p.estimatedTimeInSeconds);
        const remainingHours = estimatedHours - baseline;
        const meerwerkHours = secondsToHours(p.meerwerkSeconds ?? 0);
        const dhzHours      = secondsToHours(p.dhzSeconds ?? 0);

        return (
            <ExpandableRow key={p.id} Title={p.name} total={remainingHours}>
                <div className='flex justify-center'>
                    <ExpendableBox
                        about={p.about}
                        name={p.name}
                        id={p.id}
                        project={p}
                        link={p.links}
                        onDelete={() => deleteProjectCall(p, setLoginData)}
                        renderButtons={renderButtons(p)}
                        toDoButton={<ToDoList project={p} link={p.links} />}
                        notesButton={<ProjectNotesModal project={p} />}
                    >
                        <div className='flex flex-col px-10 text-base gap-1'>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                                <button
                                    style={statusButton(p.materialsDelivered, '#00d9ff')}
                                    onClick={(e) => { e.stopPropagation(); handleMaterialsToggle(p); }}
                                >
                                    <span style={checkbox(p.materialsDelivered, '#00d9ff')}>
                                        {p.materialsDelivered ? '✓' : ''}
                                    </span>
                                    {t('materialsDelivered')}
                                </button>

                                <button
                                    style={statusButton(meerwerkHours > 0, '#1976d2')}
                                    onClick={(e) => { e.stopPropagation(); setModalState({ projectId: p.id, type: 'meerwerk' }); }}
                                >
                                    <span style={dot('#1976d2')} />
                                    {t('meerwerk')}
                                    {meerwerkHours > 0 ? (
                                        <span style={{ marginLeft: '2px', opacity: 0.85 }}>{meerwerkHours.toFixed(2)}u</span>
                                    ) : (
                                        <span style={{ opacity: 0.6, fontWeight: 400 }}>+ toevoegen</span>
                                    )}
                                </button>

                                <button
                                    style={statusButton(dhzHours > 0, '#2e7d32')}
                                    onClick={(e) => { e.stopPropagation(); setModalState({ projectId: p.id, type: 'dhz' }); }}
                                >
                                    <span style={dot('#2e7d32')} />
                                    {t('dhzWork')}
                                    {dhzHours > 0 ? (
                                        <span style={{ marginLeft: '2px', opacity: 0.85 }}>{dhzHours.toFixed(2)}u</span>
                                    ) : (
                                        <span style={{ opacity: 0.6, fontWeight: 400 }}>+ toevoegen</span>
                                    )}
                                </button>
                            </div>

                            <div className='flex flex-row justify-between'>
                                <div className='text-lg font-bold'>{t('mechanics')}</div>
                                <div>
                                    <TotalHourOverview
                                        estimatedTimeInSeconds={p.estimatedTimeInSeconds}
                                        project={p}
                                        link={p.links}
                                    />
                                </div>
                            </div>

                            {hasTypes
                                ? <>
                                    {renderTypedMechanics(p)}
                                    {renderUntypedMechanics(p)}
                                </>
                                : p.links?.sort(sortByMechanicName).map((x) => (
                                    <LinkView key={x.id} link={x} project={p} />
                                ))
                            }
                        </div>
                    </ExpendableBox>
                </div>
            </ExpandableRow>
        );
    };

    const renderGrouped = () => {
        const grouped = new Map<string, Project[]>();
        for (const p of projects) {
            const key = getProjectMonthKey(p);
            if (!grouped.has(key)) grouped.set(key, []);
            grouped.get(key)!.push(p);
        }
        const sortedKeys = Array.from(grouped.keys()).sort((a, b) => {
            if (a === 'no-data') return 1;
            if (b === 'no-data') return -1;
            return a.localeCompare(b);
        });
        return sortedKeys.map((key) => {
            const collapsed = collapsedMonths.has(key);
            return (
                <div key={key}>
                    <div
                        onClick={() => toggleMonth(key)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            fontSize: '13px',
                            fontWeight: 700,
                            color: 'var(--accent-cyan)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            padding: '16px 4px 6px',
                            borderBottom: '1px solid var(--border-color)',
                            marginBottom: collapsed ? '0' : '8px',
                            cursor: 'pointer',
                            userSelect: 'none',
                        }}
                    >
                        {formatMonthKey(key)}
                        <svg
                            width="14" height="14" viewBox="0 0 24 24"
                            fill="none" stroke="#1976d2" strokeWidth="2.5"
                            strokeLinecap="round" strokeLinejoin="round"
                            style={{
                                transition: 'transform 0.2s ease',
                                transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                                flexShrink: 0,
                            }}
                        >
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </div>
                    {!collapsed && grouped.get(key)!.map(renderProject)}
                </div>
            );
        });
    };

    return (
        <div className='w-full'>
            <FilterProjectList query={query} setQuery={setQuery} />

            {renderGrouped()}

            {modalState && activeModal && (
                <ProjectExtrasModal
                    open={true}
                    onClose={() => setModalState(null)}
                    label={modalState.type === 'meerwerk' ? t('meerwerk') : t('dhzWork')}
                    accentColor={modalState.type === 'meerwerk' ? '#1976d2' : '#2e7d32'}
                    initialHours={
                        modalState.type === 'meerwerk'
                            ? secondsToHours(activeModal.meerwerkSeconds ?? 0)
                            : secondsToHours(activeModal.dhzSeconds ?? 0)
                    }
                    onSave={(hours) => handleSaveExtras(activeModal, modalState.type, hours)}
                />
            )}
        </div>
    );
};

export default ProjectList;
